import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff } from 'lucide-react';
import PageHeader from '@/components/ui/page-header';
import { useAuth } from "@/contexts/AuthContext";
import { login, register } from '@/api/auth'
import { useQueryClient } from '@tanstack/react-query';
import { ResponsiveContainer } from '@/components/ResponsiveContainer';

const Register = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const { setToken, setAuthenticated, setUser } = useAuth();

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);

    setErrors({}); // Clear any previous errors
    const regResponse = await register(formData.email, formData.password, '', '', ''); // Call your register API here
    if (!regResponse) {
      setErrors({ email: 'Registration failed. Please try again.' });
      setIsLoading(false);
      return;
    } 
    // If registration is successful, proceed to login
    setErrors({}); // Clear any previous errors
    const authResponse = await login(formData.email, formData.password); // get user info from backend
    if (authResponse && typeof authResponse === 'object' && 'token' in authResponse) {
      setToken(authResponse.token);
      setAuthenticated(true);
      
      // Fetch user data
      const { fetchCurrentUser } = await import('@/api/user');
      const user = await fetchCurrentUser();
      setUser(user);
      queryClient.setQueryData(['currentUser'], user);

      // Navigate to module router which will determine the correct route
      // based on the user's current in-progress module
      navigate('/module');
    } else {
      setErrors({ email: 'Invalid email or password' });
    }
      
    setIsLoading(false);
      navigate('/profile');
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="min-h-screen bg-white pb-20 safe-area-inset">
      <PageHeader 
        title="Register"
        variant="title-only"
      />

      {/* Content */}
      <ResponsiveContainer maxWidth="lg" className="flex-1 flex flex-col justify-start pt-8 sm:pt-16 md:pt-24">
        <div className="mb-8">
          <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold text-black mb-6 leading-tight">
            Sign up
          </h2>
          <p className="text-lg sm:text-xl lg:text-2xl text-[#666666] mb-8 leading-relaxed">
            Create your account to get started.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 lg:max-w-md">
          <div>
            <Label htmlFor="email" className="text-black font-medium text-lg">
              Email Address
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={`mt-2 h-12 sm:h-14 rounded-lg text-base sm:text-lg ${errors.email ? 'border-red-500' : 'border-[#cccccc]'}`}
              placeholder="Enter your email address"
              autoComplete="email"
              autoFocus
            />
            {errors.email && (
              <p className="text-red-500 text-sm sm:text-base mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <Label htmlFor="password" className="text-black font-medium text-lg">
              Password
            </Label>
            <div className="relative mt-2">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                className={`h-12 sm:h-14 pr-12 rounded-lg text-base sm:text-lg ${errors.password ? 'border-red-500' : 'border-[#cccccc]'}`}
                placeholder="Create a password"
                autoComplete="new-password"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#666666] hover:text-black transition-colors"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            {errors.password && (
              <p className="text-red-500 text-sm sm:text-base mt-1">{errors.password}</p>
            )}
          </div>

          <div>
            <Label htmlFor="confirmPassword" className="text-black font-medium text-lg">
              Confirm Password
            </Label>
            <div className="relative mt-2">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                className={`h-12 sm:h-14 pr-12 rounded-lg text-base sm:text-lg ${errors.confirmPassword ? 'border-red-500' : 'border-[#cccccc]'}`}
                placeholder="Confirm your password"
                autoComplete="new-password"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#666666] hover:text-black transition-colors"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            {errors.confirmPassword && (
              <p className="text-red-500 text-sm sm:text-base mt-1">{errors.confirmPassword}</p>
            )}
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-black text-white hover:bg-[#666666] h-12 sm:h-14 rounded-lg text-base sm:text-lg font-medium min-touch-target touch-manipulation transition-colors"
          >
            {isLoading ? 'Creating Account...' : 'Create Account'}
          </Button>
        </form>

        <div className="text-center mt-6 space-y-4 lg:max-w-md">
          <p className="text-sm sm:text-base lg:text-lg text-[#666666] leading-relaxed">
            By creating an account, you agree to our terms of service and privacy policy. 
            Your information will be used to provide personalized coaching support.
          </p>
          
          <p className="text-sm sm:text-base lg:text-lg text-[#666666]">
            Already have an account?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-black font-medium hover:underline transition-all"
            >
              Sign in
            </button>
          </p>
        </div>
      </ResponsiveContainer>
    </div>
  );
};

export default Register;
